import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { 
  ArrowLeft, Send, Video, Monitor, Clock, User, MessageSquare,
  AlertCircle, CheckCircle2, Edit2, Save, X, Star, Languages, Mail, MailOpen, Reply,
  DollarSign, CreditCard, FileText, ShieldCheck, AlertTriangle, Paperclip, ImageIcon
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const priorityColors = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-blue-100 text-blue-700",
  high: "bg-amber-100 text-amber-700",
  urgent: "bg-red-100 text-red-700",
};

const statusColors = {
  open: "bg-blue-100 text-blue-700",
  in_progress: "bg-amber-100 text-amber-700",
  pending_client: "bg-purple-100 text-purple-700",
  resolved: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-100 text-slate-700",
};

const statusLabels = {
  open: "Open",
  in_progress: "In Progress",
  pending_client: "Awaiting Response",
  resolved: "Resolved",
  closed: "Closed",
};

export default function TicketDetail() {
  const { ticketId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const { language, t } = useLanguage();

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [consultants, setConsultants] = useState([]);
  const [review, setReview] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [translatedMessages, setTranslatedMessages] = useState({});
  const [translatingId, setTranslatingId] = useState(null);
  const [emailReply, setEmailReply] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailReplyOpen, setEmailReplyOpen] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [requestingInvoice, setRequestingInvoice] = useState(false);
  const [clientContracts, setClientContracts] = useState([]);
  const [sendingPaymentRequest, setSendingPaymentRequest] = useState(false);
  const [paymentRequestMessage, setPaymentRequestMessage] = useState("");

  const handleTranslateMessage = async (messageId, text) => {
    if (translatedMessages[messageId]) {
      setTranslatedMessages((prev) => {
        const copy = { ...prev };
        delete copy[messageId];
        return copy;
      });
      return;
    }
    setTranslatingId(messageId);
    try {
      const res = await apiClient.post("/translate", {
        text,
        target_language: language,
      });
      setTranslatedMessages((prev) => ({
        ...prev,
        [messageId]: res.data.translated_text,
      }));
    } catch {
      toast.error("Translation failed");
    } finally {
      setTranslatingId(null);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [ticketRes, messagesRes] = await Promise.all([
        apiClient.get(`/tickets/${ticketId}`),
        apiClient.get(`/tickets/${ticketId}/messages`),
      ]);
      setTicket(ticketRes.data);
      setMessages(messagesRes.data);
      setEditData(ticketRes.data);

      // Fetch client's SLA contracts for assignment
      if (["admin", "consultant"].includes(user?.role) && ticketRes.data.client_id) {
        try {
          const contractsRes = await apiClient.get("/sla-contracts");
          setClientContracts(contractsRes.data.filter(c => c.client_id === ticketRes.data.client_id && c.is_active));
        } catch {}
      }

      // Fetch consultants for assignment (admin/consultant only)
      if (["admin", "consultant"].includes(user?.role)) {
        try {
          const usersRes = await apiClient.get("/users/consultants");
          setConsultants(usersRes.data);
        } catch (e) {
          console.log("Could not fetch consultants list");
        }
      }
    } catch (error) {
      toast.error("Failed to load ticket");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- apiClient is module-level, state setters are stable
  }, [ticketId, navigate, user?.role]);

  // Fetch review
  useEffect(() => {
    const fetchReview = async () => {
      try {
        const res = await apiClient.get(`/tickets/${ticketId}/review`);
        setReview(res.data);
      } catch { /* no review yet */ }
    };
    if (ticketId) fetchReview();
  }, [ticketId]);

  const handleSubmitReview = async () => {
    if (reviewRating === 0) { toast.error("Please select a rating"); return; }
    setSubmittingReview(true);
    try {
      const res = await apiClient.post(`/tickets/${ticketId}/review`, { rating: reviewRating, comment: reviewComment || null });
      setReview(res.data);
      toast.success("Review submitted! Thank you.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmittingReview(false);
    }
  };

  // Poll for new messages every 3 seconds
  const fetchMessages = useCallback(async () => {
    try {
      const res = await apiClient.get(`/tickets/${ticketId}/messages`);
      setMessages((prev) => {
        if (res.data.length !== prev.length) return res.data;
        return prev;
      });
    } catch {
      // silent fail on poll
    }
  }, [ticketId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(fetchMessages, 3000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !pendingAttachment) return;

    setSending(true);
    try {
      await apiClient.post(`/tickets/${ticketId}/messages`, {
        content: newMessage.trim() || (pendingAttachment ? `[Attachment: ${pendingAttachment.file_name}]` : ""),
        attachment_url: pendingAttachment?.attachment_url || null,
      });
      setNewMessage("");
      setPendingAttachment(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await apiClient.post(`/tickets/${ticketId}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPendingAttachment(res.data);
      toast.success(`${file.name} attached — send your message`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    }
    // Reset file input
    e.target.value = "";
  };

  const handleSendEmailReply = async (e) => {
    e.preventDefault();
    if (!emailReply.trim()) return;

    setSendingEmail(true);
    try {
      const res = await apiClient.post(`/tickets/${ticketId}/email-reply`, {
        content: emailReply.trim(),
      });
      if (res.data.status === "sent") {
        toast.success(`Email sent to ${res.data.email_sent_to}`);
        setEmailReply("");
        setEmailReplyOpen(false);
        fetchData();
      } else {
        toast.error(`Email failed: ${res.data.error || "Unknown error — check SendGrid config"}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleUpdateTicket = async () => {
    try {
      await apiClient.patch(`/tickets/${ticketId}`, {
        status: editData.status,
        priority: editData.priority,
        assigned_to: editData.assigned_to,
      });
      toast.success("Ticket updated");
      setEditing(false);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to update ticket";
      if (detail.includes("Payment") || detail.includes("payment")) {
        toast.error(detail);
      } else {
        toast.error(detail);
      }
    }
  };

  const handleStartVideoCall = async () => {
    try {
      const res = await apiClient.post("/video-rooms", {
        ticket_id: ticketId,
        title: `Video Call - ${ticket.title}`,
      });
      navigate(`/video/${res.data.room_code}`);
    } catch (error) {
      toast.error("Failed to create video room");
    }
  };

  const handleStartRemoteSession = async () => {
    try {
      const res = await apiClient.post("/remote-sessions", {
        ticket_id: ticketId,
        client_id: ticket.client_id,
      });
      const sessionCode = res.data.session_code;
      
      // Auto-send email link to client
      try {
        await apiClient.post(`/remote-sessions/${sessionCode}/send-link`);
        toast.success("Remote session link emailed to client");
      } catch {
        toast.info("Session created — copy the link manually to send to client");
      }
      
      navigate(`/remote/${sessionCode}`);
    } catch (error) {
      toast.error("Failed to create remote session");
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-slate-500">Loading ticket...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (!ticket) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Ticket not found</p>
          <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">
            Go Back
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const isStaff = ["admin", "consultant"].includes(user?.role);

  const handleConfirmPayment = async () => {
    setConfirmingPayment(true);
    try {
      await apiClient.post(`/tickets/${ticketId}/confirm-payment`, {
        reference: "admin_manual",
        method: "admin_confirmed",
      });
      toast.success("Payment confirmed for this ticket");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to confirm payment");
    } finally {
      setConfirmingPayment(false);
    }
  };

  const handleRequestInvoice = async () => {
    setRequestingInvoice(true);
    try {
      const res = await apiClient.post(`/tickets/${ticketId}/request-invoice`);
      toast.success(res.data.message || "Invoice requested");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request invoice");
    } finally {
      setRequestingInvoice(false);
    }
  };

  const handleSendPaymentRequest = async () => {
    setSendingPaymentRequest(true);
    try {
      const res = await apiClient.post(`/tickets/${ticketId}/send-payment-request`, {
        message: paymentRequestMessage,
      });
      toast.success(res.data.message || "Payment request sent to client");
      setPaymentRequestMessage("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send payment request");
    } finally {
      setSendingPaymentRequest(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="ticket-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-bold text-slate-900">{ticket.title}</h1>
            <p className="text-slate-500 mt-1">Ticket #{ticket.id.slice(0, 8)}</p>
          </div>
          {isStaff && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleStartVideoCall} data-testid="start-video-call">
                <Video className="h-4 w-4 mr-2" />
                Video Call
              </Button>
              <Button variant="outline" onClick={handleStartRemoteSession} data-testid="start-remote-session">
                <Monitor className="h-4 w-4 mr-2" />
                Remote Access
              </Button>
            </div>
          )}
        </div>

        {/* Payment Gate Warning Banner */}
        {ticket.payment_status === "pending_payment" && (
          <Card className="border-2 border-red-400 bg-red-50" data-testid="payment-gate-warning">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-bold text-red-800">NO WORK WILL COMMENCE — Payment Required</p>
                <p className="text-sm text-red-600 mt-1">
                  {isStaff
                    ? "This is an ad-hoc (non-SLA) ticket. Do NOT assign a consultant or begin any work until the client has completed payment. Only SLA clients receive automatic service."
                    : "As a non-SLA client, payment must be confirmed before any diagnostics, troubleshooting, or service delivery can begin. Please use the Pay Now button or request an invoice below."
                  }
                </p>
                {ticket.diagnostic_fee && (
                  <p className="text-sm text-red-700 mt-1 font-medium">Diagnostic Fee: ${ticket.diagnostic_fee?.toFixed(2)}</p>
                )}
                {isStaff && (
                  <button
                    onClick={async () => {
                      if (!window.confirm("Override this ticket to SLA Covered? Only do this if the client has an active SLA contract.")) return;
                      try {
                        const res = await apiClient.patch(`/tickets/${ticketId}`, { payment_status: "covered_by_sla" });
                        if (res.data && res.data.payment_status === "covered_by_sla") {
                          toast.success("Payment status changed to SLA Covered");
                        } else {
                          toast.success("Override applied");
                        }
                        fetchTicket();
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Failed to override payment status");
                      }
                    }}
                    className="mt-2 text-xs bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700"
                    data-testid="override-to-sla"
                  >
                    Override: Client has SLA
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {ticket.payment_status === "covered_by_sla" && (
          <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg" data-testid="sla-covered-notice">
            <ShieldCheck className="h-4 w-4 text-blue-600" />
            <span className="text-sm text-blue-700 font-medium">SLA Covered — Service is prepaid under active agreement</span>
          </div>
        )}

        {ticket.payment_status === "covered_by_product" && (
          <div className="flex items-center gap-2 px-4 py-2 bg-teal-50 border border-teal-200 rounded-lg" data-testid="product-covered-notice">
            <CheckCircle2 className="h-4 w-4 text-teal-600" />
            <span className="text-sm text-teal-700 font-medium">Product Support — Tech support included with your product purchase</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Messages */}
          <div className="lg:col-span-2 space-y-6">
            {/* Email Correspondence (for email-sourced tickets) */}
            {ticket.source === "email" ? (
              <Card className="sag-card border-blue-200" data-testid="email-correspondence">
                <CardHeader className="border-b border-slate-100 bg-slate-50/50">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-heading flex items-center gap-2">
                      <MailOpen className="h-5 w-5 text-blue-600" />
                      Email Correspondence
                    </CardTitle>
                    <Badge className="bg-blue-100 text-blue-700" variant="secondary">Via Email</Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {/* Email headers */}
                  <div className="px-5 py-3 border-b border-slate-100 space-y-1.5 text-sm">
                    {ticket.original_email?.from && (
                      <div className="flex gap-2">
                        <span className="text-slate-500 font-medium w-16 shrink-0">From:</span>
                        <span className="text-slate-900">{ticket.original_email.from}</span>
                      </div>
                    )}
                    {ticket.original_email?.to && (
                      <div className="flex gap-2">
                        <span className="text-slate-500 font-medium w-16 shrink-0">To:</span>
                        <span className="text-slate-900">{ticket.original_email.to}</span>
                      </div>
                    )}
                    {!ticket.original_email?.from && ticket.client_email && (
                      <div className="flex gap-2">
                        <span className="text-slate-500 font-medium w-16 shrink-0">From:</span>
                        <span className="text-slate-900">{ticket.client_name} &lt;{ticket.client_email}&gt;</span>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <span className="text-slate-500 font-medium w-16 shrink-0">Subject:</span>
                      <span className="text-slate-900 font-medium">{ticket.title}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-slate-500 font-medium w-16 shrink-0">Date:</span>
                      <span className="text-slate-700">{new Date(ticket.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  {/* Email body */}
                  <div className="px-5 py-4">
                    <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{ticket.description}</p>
                  </div>

                  {/* Reply via Email button / composer */}
                  {isStaff && (
                    <div className="border-t border-slate-100">
                      {!emailReplyOpen ? (
                        <div className="px-5 py-3">
                          <Button
                            variant="outline"
                            onClick={() => setEmailReplyOpen(true)}
                            className="border-blue-200 text-blue-700 hover:bg-blue-50"
                            data-testid="open-email-reply"
                          >
                            <Reply className="h-4 w-4 mr-2" />
                            Reply via Email to {ticket.client_email}
                          </Button>
                        </div>
                      ) : (
                        <form onSubmit={handleSendEmailReply} className="px-5 py-4 space-y-3 bg-blue-50/30" data-testid="email-reply-form">
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Mail className="h-4 w-4 text-blue-600" />
                            <span>Replying to <strong>{ticket.client_email}</strong></span>
                          </div>
                          <Textarea
                            placeholder="Type your email reply..."
                            value={emailReply}
                            onChange={(e) => setEmailReply(e.target.value)}
                            rows={5}
                            className="bg-white border-slate-200 focus:border-blue-300"
                            data-testid="email-reply-input"
                          />
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-slate-400">This will be sent as an email via SendGrid</p>
                            <div className="flex gap-2">
                              <Button type="button" variant="ghost" size="sm" onClick={() => { setEmailReplyOpen(false); setEmailReply(""); }}>
                                Cancel
                              </Button>
                              <Button
                                type="submit"
                                disabled={sendingEmail || !emailReply.trim()}
                                className="bg-blue-600 hover:bg-blue-700"
                                data-testid="send-email-reply-button"
                              >
                                <Mail className="h-4 w-4 mr-2" />
                                {sendingEmail ? "Sending..." : "Send Email"}
                              </Button>
                            </div>
                          </div>
                        </form>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              /* Standard Description for portal tickets */
              <Card className="sag-card">
                <CardHeader>
                  <CardTitle className="text-lg font-heading">Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 whitespace-pre-wrap">{ticket.description}</p>
                </CardContent>
              </Card>
            )}

            {/* Messages */}
            <Card className="sag-card">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  {t("tickets.conversation")}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[400px] p-4">
                  {messages.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      {t("tickets.noMessages")}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((message) => {
                        const isOwn = message.sender_id === user?.id;
                        const isEmailReply = message.source === "email_reply";
                        const isEmailInbound = message.source === "email";
                        const isTranslated = !!translatedMessages[message.id];
                        const isTranslating = translatingId === message.id;
                        return (
                          <div
                            key={message.id}
                            className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
                          >
                            <div
                              className={`max-w-[80%] rounded-xl px-4 py-3 ${
                                isOwn
                                  ? isEmailReply ? "bg-blue-700 text-white" : "bg-sag-navy text-white"
                                  : isEmailInbound ? "bg-blue-50 text-slate-900 border border-blue-200" : "bg-slate-100 text-slate-900"
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-xs font-medium ${isOwn ? "text-white/80" : "text-slate-500"}`}>
                                  {message.sender_name}
                                </span>
                                <Badge 
                                  variant="secondary" 
                                  className={`text-[10px] ${isOwn ? "bg-white/20 text-white" : ""}`}
                                >
                                  {message.sender_role}
                                </Badge>
                                {(isEmailReply || isEmailInbound) && (
                                  <Mail className={`h-3 w-3 ${isOwn ? "text-white/60" : "text-blue-500"}`} />
                                )}
                              </div>
                              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                              {message.attachment_url && (
                                <div className="mt-2">
                                  {message.attachment_url.match(/\.(png|jpg|jpeg|webp|gif)$/i) ? (
                                    <a href={`${process.env.REACT_APP_BACKEND_URL}${message.attachment_url}`} target="_blank" rel="noopener noreferrer" data-testid={`attachment-img-${message.id}`}>
                                      <img
                                        src={`${process.env.REACT_APP_BACKEND_URL}${message.attachment_url}`}
                                        alt="Attachment"
                                        className="max-w-xs max-h-48 rounded-lg border border-white/20 cursor-pointer hover:opacity-90 transition-opacity"
                                      />
                                    </a>
                                  ) : (
                                    <a
                                      href={`${process.env.REACT_APP_BACKEND_URL}${message.attachment_url}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md ${isOwn ? "bg-white/20 text-white hover:bg-white/30" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                                      data-testid={`attachment-file-${message.id}`}
                                    >
                                      <Paperclip className="h-3 w-3" />
                                      View Attachment
                                    </a>
                                  )}
                                </div>
                              )}
                              {isTranslated && (
                                <div className={`mt-2 pt-2 border-t ${isOwn ? "border-white/20" : "border-slate-200"}`}>
                                  <p className={`text-[10px] font-medium mb-1 ${isOwn ? "text-white/60" : "text-slate-400"}`}>
                                    {t("tickets.translated")}:
                                  </p>
                                  <p className="text-sm whitespace-pre-wrap">{translatedMessages[message.id]}</p>
                                </div>
                              )}
                              <div className={`flex items-center justify-between mt-2`}>
                                <p className={`text-[10px] ${isOwn ? "text-white/60" : "text-slate-400"}`}>
                                  {new Date(message.created_at).toLocaleString()}
                                </p>
                                <button
                                  type="button"
                                  onClick={() => handleTranslateMessage(message.id, message.content)}
                                  disabled={isTranslating}
                                  className={`flex items-center gap-1 text-[10px] font-medium rounded px-1.5 py-0.5 transition-colors ${
                                    isOwn
                                      ? "text-white/70 hover:text-white hover:bg-white/10"
                                      : "text-slate-400 hover:text-slate-700 hover:bg-slate-200"
                                  } ${isTranslating ? "opacity-50" : ""}`}
                                  data-testid={`translate-msg-${message.id}`}
                                >
                                  <Languages className="h-3 w-3" />
                                  {isTranslating ? t("tickets.translating") : isTranslated ? t("tickets.showOriginal") : t("tickets.translate")}
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </ScrollArea>
                <Separator />
                {pendingAttachment && (
                  <div className="px-4 pt-2 flex items-center gap-2 text-sm bg-blue-50 border-b border-blue-100">
                    <Paperclip className="h-3.5 w-3.5 text-blue-600" />
                    <span className="text-blue-700 truncate flex-1">{pendingAttachment.file_name}</span>
                    <button onClick={() => setPendingAttachment(null)} className="p-0.5 hover:bg-blue-200 rounded" data-testid="remove-attachment">
                      <X className="h-3.5 w-3.5 text-blue-400" />
                    </button>
                  </div>
                )}
                <form onSubmit={handleSendMessage} className="p-4 flex gap-2 items-center">
                  <input
                    type="file"
                    id="chat-file-upload"
                    className="hidden"
                    accept="image/png,image/jpeg,image/webp,image/gif,application/pdf"
                    onChange={handleFileUpload}
                    data-testid="chat-file-input"
                  />
                  <button
                    type="button"
                    onClick={() => document.getElementById("chat-file-upload").click()}
                    className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
                    title="Attach image or file"
                    data-testid="attach-file-button"
                  >
                    <Paperclip className="h-5 w-5" />
                  </button>
                  <Input
                    placeholder={t("tickets.typeMessage")}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    className="flex-1"
                    data-testid="message-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={sending || (!newMessage.trim() && !pendingAttachment)}
                    className="bg-sag-navy hover:bg-sag-navy-800"
                    data-testid="send-message-button"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Ticket Info */}
          <div className="space-y-6">
            <Card className="sag-card">
              <CardHeader className="border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-heading">Details</CardTitle>
                  {isStaff && !editing && (
                    <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  )}
                  {editing && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={handleUpdateTicket}>
                        <Save className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                {/* Status */}
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase">Status</label>
                  {editing ? (
                    <Select
                      value={editData.status}
                      onValueChange={(v) => setEditData({ ...editData, status: v })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="pending_client">Pending Client</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge className={`mt-1 ${statusColors[ticket.status]}`}>
                      {statusLabels[ticket.status]}
                    </Badge>
                  )}
                </div>

                {/* Payment Status */}
                <div data-testid="payment-status-section">
                  <label className="text-xs font-medium text-slate-500 uppercase">Payment Status</label>
                  <div className="mt-1">
                    {ticket.payment_status === "pending_payment" && (
                      <Badge className="bg-red-100 text-red-700 border border-red-200" data-testid="payment-status-badge">
                        <DollarSign className="h-3 w-3 mr-1" /> Payment Required
                      </Badge>
                    )}
                    {ticket.payment_status === "paid" && (
                      <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid="payment-status-badge">
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Paid
                      </Badge>
                    )}
                    {ticket.payment_status === "covered_by_sla" && (
                      <Badge className="bg-blue-100 text-blue-700 border border-blue-200" data-testid="payment-status-badge">
                        <ShieldCheck className="h-3 w-3 mr-1" /> SLA Covered
                      </Badge>
                    )}
                    {ticket.payment_status === "covered_by_product" && (
                      <Badge className="bg-teal-100 text-teal-700 border border-teal-200" data-testid="payment-status-badge">
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Product Support
                      </Badge>
                    )}
                    {!ticket.payment_status && (
                      <Badge className="bg-slate-100 text-slate-600" data-testid="payment-status-badge">
                        Unknown
                      </Badge>
                    )}
                  </div>
                  
                  {/* Payment action buttons */}
                  {ticket.payment_status === "pending_payment" && (
                    <div className="mt-3 space-y-2">
                      {ticket.diagnostic_fee && (
                        <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-center">
                          <p className="text-xs text-amber-700 font-medium">Diagnostic Fee</p>
                          <p className="text-lg font-bold text-amber-800">${ticket.diagnostic_fee?.toFixed(2)}</p>
                        </div>
                      )}

                      {/* Staff actions: Send Payment Request + Confirm Payment */}
                      {isStaff && (
                        <>
                          <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-2">
                            <p className="text-xs font-semibold text-slate-600 uppercase">Notify Client</p>
                            <textarea
                              rows={2}
                              placeholder="Optional message to client..."
                              className="w-full text-sm border border-slate-200 rounded-md p-2 resize-none focus:ring-1 focus:ring-sag-navy focus:border-sag-navy"
                              value={paymentRequestMessage}
                              onChange={(e) => setPaymentRequestMessage(e.target.value)}
                              data-testid="payment-request-message"
                            />
                            <Button
                              size="sm"
                              className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                              onClick={handleSendPaymentRequest}
                              disabled={sendingPaymentRequest}
                              data-testid="send-payment-request-btn"
                            >
                              <Mail className="h-4 w-4 mr-1.5" />
                              {sendingPaymentRequest ? "Sending..." : ticket.payment_request_sent ? "Resend Payment Link" : "Send Payment Link to Client"}
                            </Button>
                            {ticket.payment_request_sent && (
                              <p className="text-xs text-slate-400 text-center">
                                Sent {ticket.payment_request_sent_at ? new Date(ticket.payment_request_sent_at).toLocaleString() : ""} by {ticket.payment_request_sent_by || "staff"}
                              </p>
                            )}
                          </div>
                          {user?.role === "admin" && (
                            <Button
                              size="sm"
                              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={handleConfirmPayment}
                              disabled={confirmingPayment}
                              data-testid="admin-confirm-payment-btn"
                            >
                              <CheckCircle2 className="h-4 w-4 mr-1.5" />
                              {confirmingPayment ? "Confirming..." : "Confirm Payment Received"}
                            </Button>
                          )}
                        </>
                      )}

                      {/* Client actions: Pay Now + Request Invoice */}
                      {user?.role === "client" && (
                        <>
                          <Button
                            size="sm"
                            className="w-full bg-sag-navy hover:bg-sag-navy-800"
                            onClick={() => navigate("/services")}
                            data-testid="ticket-pay-now-btn"
                          >
                            <CreditCard className="h-4 w-4 mr-1.5" /> Pay Now
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full"
                            onClick={handleRequestInvoice}
                            disabled={requestingInvoice || ticket.invoice_requested}
                            data-testid="ticket-request-invoice-btn"
                          >
                            <FileText className="h-4 w-4 mr-1.5" />
                            {ticket.invoice_requested ? "Invoice Requested" : "Request Invoice"}
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Log Time for SLA */}
                {isStaff && (ticket.payment_status === "covered_by_sla" || ticket.payment_status === "paid") && (
                  <div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                      onClick={() => navigate(`/time-tracking?ticket_id=${ticketId}&client_id=${ticket.client_id}`)}
                      data-testid="log-time-btn"
                    >
                      <Clock className="h-4 w-4 mr-1.5" /> Log Time to SLA
                    </Button>
                  </div>
                )}

                {/* SLA Contract Assignment */}
                {isStaff && clientContracts.length > 0 && (
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase">SLA Contract</label>
                    <Select
                      value={ticket.sla_contract_id || "none"}
                      onValueChange={async (v) => {
                        try {
                          await apiClient.patch(`/tickets/${ticketId}`, { sla_contract_id: v === "none" ? null : v });
                          toast.success("SLA contract updated");
                          fetchData();
                        } catch { toast.error("Failed to update SLA contract"); }
                      }}
                    >
                      <SelectTrigger className="mt-1" data-testid="sla-contract-select">
                        <SelectValue placeholder="Select SLA contract" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— No contract —</SelectItem>
                        {clientContracts.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {ticket.sla_contract_title && (
                      <p className="text-xs text-slate-500 mt-1 truncate">{ticket.sla_contract_title}</p>
                    )}
                  </div>
                )}

                <Separator />
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase">Priority</label>
                  {editing ? (
                    <Select
                      value={editData.priority}
                      onValueChange={(v) => setEditData({ ...editData, priority: v })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge className={`mt-1 ${priorityColors[ticket.priority]}`}>
                      {ticket.priority}
                    </Badge>
                  )}
                </div>

                {/* Assigned To */}
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase">Assigned To</label>
                  {editing ? (
                    <Select
                      value={editData.assigned_to || "unassigned"}
                      onValueChange={(v) => setEditData({ ...editData, assigned_to: v === "unassigned" ? null : v })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Unassigned" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {consultants.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="mt-1 text-slate-900">
                      {ticket.assigned_name || "Unassigned"}
                    </p>
                  )}
                </div>

                <Separator />

                {/* Client */}
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase">Client</label>
                  <p className="mt-1 text-slate-900 flex items-center gap-2">
                    <User className="h-4 w-4 text-slate-400" />
                    {ticket.client_name}
                  </p>
                  {ticket.client_email && (
                    <p className="mt-1 text-sm text-slate-500 flex items-center gap-2">
                      <Mail className="h-3.5 w-3.5 text-slate-400" />
                      {ticket.client_email}
                    </p>
                  )}
                </div>

                {/* Source */}
                {ticket.source && ticket.source !== "portal" && (
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase">Source</label>
                    <div className="mt-1">
                      <Badge className="bg-blue-100 text-blue-700" variant="secondary">
                        <Mail className="h-3 w-3 mr-1" />
                        {ticket.source === "email" ? "Email" : ticket.source}
                      </Badge>
                    </div>
                  </div>
                )}

                {/* Category */}
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase">Category</label>
                  <p className="mt-1 text-slate-900 capitalize">{ticket.category}</p>
                </div>

                {/* Service Type */}
                {ticket.service_type && (
                  <div data-testid="ticket-service-type">
                    <label className="text-xs font-medium text-slate-500 uppercase">Service Type</label>
                    <div className="mt-1">
                      <Badge className={
                        ticket.payment_status === "covered_by_product" 
                          ? "bg-teal-100 text-teal-700 border border-teal-200" 
                          : ticket.payment_status === "pending_payment"
                          ? "bg-amber-100 text-amber-700 border border-amber-200"
                          : "bg-slate-100 text-slate-600"
                      } variant="secondary">
                        {ticket.service_type}
                      </Badge>
                    </div>
                  </div>
                )}

                {/* Created */}
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase">Created</label>
                  <p className="mt-1 text-slate-900 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-slate-400" />
                    {new Date(ticket.created_at).toLocaleString()}
                  </p>
                </div>

                {/* Updated */}
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase">Last Updated</label>
                  <p className="mt-1 text-slate-900">
                    {new Date(ticket.updated_at).toLocaleString()}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Review Section - Client only, for resolved/closed tickets */}
            {user?.role === "client" && ["resolved", "closed"].includes(ticket.status) && ticket.assigned_to_id && (
              <Card className="sag-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <Star className="h-5 w-5 text-amber-500" /> Rate Your Experience
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {review ? (
                    <div>
                      <div className="flex gap-1 mb-2">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star key={s} className={`h-5 w-5 ${s <= review.rating ? "text-amber-500 fill-amber-500" : "text-slate-300"}`} />
                        ))}
                      </div>
                      {review.comment && <p className="text-sm text-slate-600">{review.comment}</p>}
                      <p className="text-xs text-slate-400 mt-2">Submitted {new Date(review.created_at).toLocaleDateString()}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <button key={s} type="button" onClick={() => setReviewRating(s)} className="focus:outline-none" data-testid={`star-${s}`}>
                            <Star className={`h-7 w-7 cursor-pointer transition-colors ${s <= reviewRating ? "text-amber-500 fill-amber-500" : "text-slate-300 hover:text-amber-400"}`} />
                          </button>
                        ))}
                      </div>
                      <Textarea placeholder="Share your experience (optional)..." value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} rows={2} />
                      <Button onClick={handleSubmitReview} disabled={submittingReview || reviewRating === 0} className="bg-sag-navy hover:bg-sag-navy-800" data-testid="submit-review-button">
                        {submittingReview ? "Submitting..." : "Submit Review"}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
